import { useMutation, useQueryClient, type Query } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  agentToolsApi,
  type PaginatedMCPConnectionsResponse,
} from "../../../services/agent-tools-service";
import { MCP_CONNECTIONS_QUERY_KEY } from "./useMCPConnections";

type ApiError = {
  response?: {
    data?: { error?: { message?: string }; detail?: string; message?: string };
  };
  message?: string;
};

const extractErrorMessage = (error: ApiError, fallback: string): string => {
  const data = error.response?.data;
  return (
    data?.error?.message ||
    data?.detail ||
    data?.message ||
    error.message ||
    fallback
  );
};

// Only match the paginated list keys: ["mcp-connections", page, pageSize].
// Per-id keys look like ["mcp-connections", "test", id] or
// ["mcp-connections", "detail", id] and must NOT be invalidated on delete,
// otherwise the still-mounted observer for the deleted row refetches and 404s.
const isListPageQuery = (query: Query): boolean => {
  const key = query.queryKey;
  return (
    Array.isArray(key) &&
    key[0] === MCP_CONNECTIONS_QUERY_KEY[0] &&
    typeof key[1] === "number"
  );
};

type Options = {
  onSuccess?: (id: string) => void;
};

export const useDeleteMCPConnection = (options: Options = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentToolsApi.deleteMCPConnection(id),
    onSuccess: (_data, id) => {
      toast.success(t("mcpPage.mutation.deleted"));

      // 1) Drop the deleted row from every cached list page synchronously so
      //    its <MCPListItem> unmounts on the next render and its per-id test
      //    observer detaches — no chance to fire a stale POST.
      queryClient.setQueriesData<PaginatedMCPConnectionsResponse>(
        { predicate: isListPageQuery },
        (old) => {
          if (!old || !Array.isArray(old.items)) return old;
          return {
            ...old,
            items: old.items.filter((item) => item.id !== id),
          };
        },
      );

      // 2) Let the page react (close the drawer if it was on the deleted id)
      //    before any further cache touches — this is what disables the
      //    drawer's per-id test/detail queries.
      options.onSuccess?.(id);

      // 3) Refetch the list authoritatively. Scoped to list pages so the
      //    per-id queries are never marked stale.
      queryClient.invalidateQueries({ predicate: isListPageQuery });

      // 4) Evict the now-orphaned per-id caches for the deleted connection.
      queryClient.removeQueries({
        queryKey: [...MCP_CONNECTIONS_QUERY_KEY, "test", id],
      });
      queryClient.removeQueries({
        queryKey: [...MCP_CONNECTIONS_QUERY_KEY, "detail", id],
      });
    },
    onError: (error: ApiError) => {
      toast.error(extractErrorMessage(error, t("mcpPage.mutation.deleteFailed")));
    },
  });
};
